/**
 * Puente React ⇄ Product Tour (iframe en /studio-tour/index.html). Mismo contrato que el
 * Clapper Arcade (`useArcadeBridge`), con tres cosas más: edición de textos y notas por
 * admins (van a ConfigTable versionadas), preguntas a Brain (responde el back con Haiku) y
 * tiempo transcurrido medido acá (spec 6.4: el recorrido no lo cuenta).
 *
 * - `st:ready` (el recorrido lo repite cada 500 ms) → `st:init` con usuario + contenido +
 *   progreso guardado; idempotente, un F5 del iframe re-inicializa solo.
 * - `st:event` se encola y va a reportProgress con debounce de 1,5 s (flush en pagehide).
 *   Los `ui_state` se deduplican: viaja solo el último snapshot.
 */
import { useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';
import { toastError } from '@/lib/toast-error';
import type { LearningEvent } from '@/../../shared/people-learning';
import { TOUR_MSG } from '@/../../shared/studio-tour';

export function useStudioTourBridge(iframeRef: React.RefObject<HTMLIFrameElement | null>, onExit: () => void) {
  const tour = trpc.studioTour.getMyTour.useQuery(undefined, { staleTime: 60_000, refetchOnWindowFocus: false });
  const utils = trpc.useUtils();
  const report = trpc.studioTour.reportProgress.useMutation({
    onSuccess: (res) => {
      iframeRef.current?.contentWindow?.postMessage({ type: TOUR_MSG.refresh, summary: res.summary, status: res.status }, window.location.origin);
      if (res.finishedAt) void utils.studioTour.getMyTour.invalidate();
    },
  });
  // Si el guardado falla (FORBIDDEN, red), el admin que editó tiene que enterarse (frontend-auditor 19/10).
  const saveText = trpc.studioTour.saveText.useMutation({ onError: (e) => toastError(e, 'studio.admin.save_error') });
  const saveNote = trpc.studioTour.saveNote.useMutation({ onError: (e) => toastError(e, 'studio.admin.save_error') });
  const ask = trpc.studioTour.ask.useMutation();

  const dataRef = useRef(tour.data);
  dataRef.current = tour.data;
  const queueRef = useRef<LearningEvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reportRef = useRef(report.mutate);
  reportRef.current = report.mutate;
  const saveTextRef = useRef(saveText.mutate);
  saveTextRef.current = saveText.mutate;
  const saveNoteRef = useRef(saveNote.mutate);
  saveNoteRef.current = saveNote.mutate;
  const askRef = useRef(ask.mutateAsync);
  askRef.current = ask.mutateAsync;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  // Tiempo total: lo guardado + lo que lleva esta sesión (monótono; el servidor hace max-merge).
  const sessionStartRef = useRef<number>(Date.now());
  const elapsedBaseRef = useRef<number>(0);

  useEffect(() => {
    const elapsedNow = () => elapsedBaseRef.current + (Date.now() - sessionStartRef.current);
    const flush = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (queueRef.current.length === 0) return;
      // Un solo snapshot de interfaz por batch: el último.
      const q = queueRef.current;
      const lastUi = [...q].reverse().find((e) => e.kind === 'ui_state');
      const events = q.filter((e) => e.kind !== 'ui_state').concat(lastUi ? [lastUi] : []).slice(0, 50);
      queueRef.current = [];
      reportRef.current({ events });
    };

    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const msg = e.data;
      if (!msg || typeof msg !== 'object') return;
      const win = iframeRef.current?.contentWindow;

      if (msg.type === TOUR_MSG.ready) {
        const data = dataRef.current;
        if (!data) return; // la query sigue cargando; el recorrido reintenta solo
        elapsedBaseRef.current = data.progress?.elapsedMs ?? 0;
        sessionStartRef.current = Date.now();
        win?.postMessage(
          {
            type: TOUR_MSG.init,
            user: { email: data.me.email, name: data.me.name, lastName: data.me.lastName, canEdit: data.me.canEdit, unlockAll: data.me.unlockAll, category: data.me.category },
            content: data.content,
            savedProgress: data.progress,
          },
          window.location.origin
        );
      } else if (msg.type === TOUR_MSG.event && msg.event) {
        queueRef.current.push({ ...(msg.event as LearningEvent), elapsedMs: elapsedNow() });
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, 1500);
      } else if (msg.type === TOUR_MSG.exit) {
        flush();
        onExitRef.current();
      } else if (msg.type === TOUR_MSG.saveText && typeof msg.key === 'string') {
        if (!dataRef.current?.me.canEdit) return;
        saveTextRef.current({ key: String(msg.key).slice(0, 200), value: msg.value == null ? null : String(msg.value).slice(0, 4000) });
      } else if (msg.type === TOUR_MSG.saveNote && typeof msg.stepId === 'string') {
        if (!dataRef.current?.me.canEdit) return;
        saveNoteRef.current({ stepId: String(msg.stepId).slice(0, 40), note: msg.note ?? null });
      } else if (msg.type === TOUR_MSG.ask && typeof msg.question === 'string') {
        try {
          const res = await askRef.current({ question: String(msg.question).slice(0, 1000), stepId: typeof msg.stepId === 'string' ? msg.stepId : undefined });
          win?.postMessage({ type: TOUR_MSG.answer, id: msg.id, reply: res.reply, fallback: res.fallback }, window.location.origin);
        } catch {
          win?.postMessage({ type: TOUR_MSG.answer, id: msg.id, reply: null, fallback: true }, window.location.origin);
        }
      }
    };

    const onPageHide = () => flush();
    window.addEventListener('message', onMessage);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onPageHide);
    return () => {
      flush();
      window.removeEventListener('message', onMessage);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLoading: tour.isLoading, error: tour.error };
}
